import { z } from "zod";

// Day hours schema
export const dayHoursSchema = z.object({
  day: z.enum([
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ]),
  open: z.string().optional(),
  close: z.string().optional(),
  closed: z.boolean().optional(),
});

// Location contact schema
export const locationContactSchema = z.object({
  phone: z.string().optional(),
  email: z.string().email().optional(),
  website: z.string().url().optional(),
  social: z
    .object({
      facebook: z.string().url().optional(),
      instagram: z.string().url().optional(),
      twitter: z.string().url().optional(),
    })
    .optional(),
});

// Address schema
export const addressSchema = z.object({
  street1: z.string().min(1, "Street address is required"),
  street2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  zip: z.string().min(1, "ZIP code is required"),
  country: z.string().optional(),
});

export const menuPriceSchema = z.object({
  label: z.string().min(1),
  price: z.number().min(0),
});

export const menuItemEntrySchema = z.object({
  id: z.string().optional(),
  kind: z.literal("item"),
  title: z.string().min(1),
  description: z.string().optional(),
  imageUrl: z.string().url().optional(),
  note: z.string().optional(),
  tags: z.array(z.string().min(1)).optional(),
  prices: z.array(menuPriceSchema).optional(),
});

export const menuListEntrySchema = z.object({
  id: z.string().optional(),
  kind: z.literal("list"),
  title: z.string().min(1),
  description: z.string().optional(),
  imageUrl: z.string().url().optional(),
  note: z.string().optional(),
  tags: z.array(z.string().min(1)).optional(),
});

export const menuFeaturedEntrySchema = z.object({
  id: z.string().optional(),
  kind: z.literal("featured"),
  title: z.string().min(1),
  description: z.string().optional(),
  imageUrl: z.string().url().optional(),
  note: z.string().optional(),
  tags: z.array(z.string().min(1)).optional(),
  prices: z.array(menuPriceSchema).optional(),
});

export const menuSectionEntrySchema = z.discriminatedUnion("kind", [
  menuItemEntrySchema,
  menuListEntrySchema,
  menuFeaturedEntrySchema,
]);

export const menuSectionSchema = z.object({
  id: z.string().optional(),
  title: z.string().min(1),
  description: z.string().optional(),
  imageUrl: z.string().url().optional(),
  type: z.enum(["items", "list", "featured"]),
  entries: z.array(menuSectionEntrySchema),
});

// Location schema
export const locationSchema = z.object({
  id: z.string().min(1, "Location ID is required"),
  slug: z.string().min(1, "Location slug is required"),
  name: z.string().min(1, "Location name is required"),
  description: z.string().optional(),
  shortDescription: z.string().optional(),
  menuHighlights: z.array(z.string()).optional(),
  address: addressSchema,
  phone: z.string().optional(),
  contact: locationContactSchema.optional(),
  hours: z.array(dayHoursSchema).optional(),
  hoursNote: z.string().optional(),
  comingSoon: z.boolean().optional(),
  seasonalNotes: z.string().optional(),
  amenities: z.array(z.string()).optional(),
  image: z.string().optional(),
  coordinates: z
    .object({
      lat: z.number(),
      lng: z.number(),
    })
    .optional(),
  isPrimary: z.boolean().optional(),
  menu: z
    .object({
      sections: z.array(menuSectionSchema).optional(),
    })
    .optional(),
  metadata: z.record(z.any()).optional(),
});

// Locations section content schema
export const locationsSectionContentSchema = z.object({
  title: z.string().optional(),
  subtitle: z.string().optional(),
  locations: z.array(locationSchema).min(1, "At least one location required"),
  groups: z
    .array(
      z.object({
        label: z.string().min(1, "Group label is required"),
        locations: z.array(locationSchema).min(1, "At least one location required"),
      })
    )
    .optional(),
  showMap: z.boolean().optional(),
  mapsApiKey: z.string().optional(),
  variant: z.enum(["cards", "list", "map"]).optional(),
});
